import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Loader2, Smartphone } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { vcamPhoneUrl } from './vcam';
import { useVcamLink } from './use-vcam-link';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const qrcode: any = require('../../../vendor/qrcode-generator/qrcode.js');

/**
 * "Film with my phone": the QR code that pairs a phone as the studio's camera,
 * and the link's status. The link itself lives in the studio (useVcamLink), so
 * this panel can close without dropping the phone.
 */
export const PhoneCameraPanel: React.FC<{
    link: ReturnType<typeof useVcamLink>;
    recording?: boolean;
}> = ({ link, recording }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const url = link.session ? vcamPhoneUrl(link.session) : null;
    const insecure = !!url && url.startsWith('http:') && !/^http:\/\/(localhost|127\.)/.test(url);
    const qrSvg = useMemo(() => {
        if (!url) return '';
        const qr = qrcode(0, 'M');
        qr.addData(url);
        qr.make();
        return qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
    }, [url]);

    if (!link.session) {
        return (
            <div className="space-y-3">
                <p className="text-[12px] leading-relaxed text-muted-foreground">
                    {t('VCAM.PANEL_INTRO', 'Use your phone as the camera: scan a code, then turn and walk with the phone — this view follows it live. Record takes from the phone; each becomes a scene.')}
                </p>
                <Button variant="outline" size="sm" className="w-full" onClick={link.open} disabled={link.opening}>
                    {link.opening ? <Loader2 className="size-4 animate-spin" /> : <Smartphone className="size-4" />}
                    {t('VCAM.CONNECT', 'Connect my phone')}
                </Button>
                {link.error && <p className="text-[12px] text-destructive">{link.error}</p>}
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2 text-[12px]">
                <span className={cn('size-2 rounded-full', link.phoneLinked ? 'bg-emerald-500' : 'bg-muted-foreground/40')} />
                <span className="text-foreground">
                    {link.phoneLinked ? t('VCAM.PHONE_LINKED', 'Phone linked — this view follows it') : t('VCAM.SCAN', 'Scan with your phone camera')}
                </span>
                {recording && <span className="ml-auto rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-medium text-white">REC</span>}
            </div>
            {!link.phoneLinked && (
                // eslint-disable-next-line react/no-danger
                <div className="mx-auto w-44 rounded-lg border bg-white p-2" dangerouslySetInnerHTML={{ __html: qrSvg }} />
            )}
            {insecure && (
                <p className="text-[11px] leading-relaxed text-amber-700">
                    {t('VCAM.NEEDS_HTTPS', 'iPhones only share motion and camera with secure (https) pages. Open the studio through an https address so the phone link works.')}
                </p>
            )}
            <div className="flex items-center gap-1.5">
                <code className="min-w-0 flex-1 truncate rounded-md bg-muted px-2 py-1 text-[11px] text-muted-foreground">{url}</code>
                <button
                    type="button"
                    aria-label={t('VCAM.COPY', 'Copy link')}
                    onClick={() => { navigator.clipboard?.writeText(url || '').then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => undefined); }}
                    className="flex cursor-pointer items-center rounded-md border-0 bg-transparent p-1 text-muted-foreground hover:text-foreground"
                >
                    {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                </button>
            </div>
            <Button variant="outline" size="sm" className="w-full" onClick={link.close}>
                {t('VCAM.DISCONNECT', 'Disconnect the phone')}
            </Button>
        </div>
    );
};
